import { createFileRoute, Link } from "@tanstack/react-router";
import { Bike, MapPin, Coffee, Waves, Phone } from "lucide-react";
import { canonical, faqPageSchema } from "@/lib/seo";

export const Route = createFileRoute("/cycling-friendly-accommodation-niagara")({
  head: () => ({
    meta: [
      { title: "Cycling-Friendly Accommodation in Niagara - Malak BnB Resort" },
      { name: "description", content: "Malak BnB Resort is a cycling-friendly BnB in Crystal Beach, close to Cycle and Stay Niagara routes, with flat roads, breakfast, and a pool to recover in." },
      { property: "og:title", content: "Cycling-Friendly Accommodation in Niagara - Malak BnB Resort" },
      { property: "og:description", content: "A quiet base for cycling the Niagara Region. Flat roads, Cycle and Stay Niagara routes nearby, breakfast every morning." },
      { property: "og:url", content: "https://malakbnbresort.ca/cycling-friendly-accommodation-niagara" },
      { property: "og:image", content: "/images/explore-biking.webp" },
      { "script:ld+json": faqPageSchema(FAQ) },
    ],
    links: [canonical("/cycling-friendly-accommodation-niagara")],
  }),
  component: CyclingAccommodationPage,
});

const REASONS = [
  { icon: Bike, title: "Flat, Rideable Streets", desc: "Crystal Beach and the surrounding roads are flat and quiet, easy on legs that have already put in the miles." },
  { icon: MapPin, title: "Close to Cycle and Stay Niagara", desc: "The property sits within reach of Cycle and Stay Niagara routes, connecting toward Fort Erie, Ridgeway, and the wider Niagara Region." },
  { icon: Coffee, title: "Breakfast Before You Ride", desc: "A continental breakfast buffet is included every morning, useful fuel before a longer day in the saddle." },
  { icon: Waves, title: "A Pool to Recover In", desc: "The outdoor heated pool is a good place to cool down after a ride, long before dinner." },
];

const FAQ = [
  {
    q: "Is Malak BnB Resort a good base for a cycling trip in the Niagara Region?",
    a: "Yes. Malak BnB Resort is in Crystal Beach, an area with flat, low-traffic roads close to Cycle and Stay Niagara routes. Guests use it as a quiet overnight base between longer rides toward Fort Erie, Ridgeway, and the Niagara Region.",
  },
  {
    q: "Does Malak BnB Resort have secure space for bikes overnight?",
    a: "Guests bringing bikes should mention it when submitting a reservation request so the team can advise on the best arrangement for your dates. Malak BnB Resort is a small, family-run property and can accommodate most requests when asked in advance.",
  },
  {
    q: "How far is Malak BnB Resort from Niagara Falls by bike or car?",
    a: "Niagara Falls is about a 30 minute drive from Malak BnB Resort. It is a longer ride by bike, and most cycling guests treat Crystal Beach, Fort Erie, and Ridgeway as the core loop rather than riding all the way to the falls.",
  },
  {
    q: "Is breakfast included for cycling guests at Malak BnB Resort?",
    a: "Yes. A continental breakfast buffet is included with every stay at Malak BnB Resort, available before an early start on the bike.",
  },
];

function CyclingAccommodationPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[380px] overflow-hidden">
        <img
          src="/images/explore-biking.webp"
          alt="Cycling through the streets of Crystal Beach, Ontario"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/65 via-foreground/20 to-transparent" />
        <div className="relative z-10 flex h-full items-end pb-16 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl w-full">
            <p className="text-white/70 font-semibold text-sm uppercase tracking-widest mb-3">Niagara Region, Ontario</p>
            <h1 className="text-4xl sm:text-5xl font-heading text-white leading-tight max-w-2xl">
              Cycling-Friendly Accommodation in Niagara
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-4">A Base Built for Riding Days</p>
          <p className="text-2xl font-heading text-foreground leading-snug mb-8">
            Crystal Beach is flat, quiet, and close to the routes that make the Niagara Region worth cycling.
          </p>
          <div className="prose prose-lg text-muted-foreground space-y-5 leading-relaxed">
            <p>
              Malak BnB Resort is a family-owned bed and breakfast at 264 Ridge Road South in Crystal Beach, in the
              cycling-friendly stretch of the Niagara Region near Fort Erie and Ridgeway. The roads here are flat,
              the traffic is light, and the property sits close to Cycle and Stay Niagara routes without being
              tangled up in the busier corridor further north toward Niagara Falls.
            </p>
            <p>
              After a ride, there is an outdoor heated pool to cool off in, and a continental breakfast buffet
              waiting each morning before you head back out. It is a straightforward, comfortable base rather than
              a resort built around cycling specifically, and that simplicity is part of what guests come back for.
            </p>
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/book"
              className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Request to Book
            </Link>
            <Link
              to="/rooms"
              className="inline-flex items-center justify-center rounded-full border border-border px-8 py-3 text-sm font-semibold text-foreground hover:bg-muted transition-colors"
            >
              View Rooms and Rates
            </Link>
          </div>
        </div>
      </section>

      {/* Reasons */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Why Cyclists Stay Here</p>
            <h2 className="text-3xl font-heading text-foreground">Built Around a Riding Day</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {REASONS.map((item) => (
              <div key={item.title} className="bg-background rounded-2xl p-6 border border-border/50 text-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-heading text-base text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-muted-foreground leading-relaxed mb-4">
            Planning a full day on two wheels around Crystal Beach itself?
          </p>
          <Link to="/blog/exploring-crystal-beach-by-bike" className="text-primary font-semibold text-sm hover:underline">
            Read the guide to exploring Crystal Beach by bike →
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Common Questions</p>
            <h2 className="text-3xl font-heading text-foreground">Cycling and Your Stay</h2>
          </div>
          <div className="space-y-8">
            {FAQ.map(({ q, a }) => (
              <div key={q} className="border-b border-border pb-8 last:border-0 last:pb-0">
                <h3 className="font-heading text-lg text-foreground mb-3">{q}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-foreground">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-primary-foreground/50 font-semibold text-sm uppercase tracking-widest mb-4">Niagara Region, Ontario</p>
          <h2 className="text-3xl sm:text-4xl font-heading text-primary-foreground leading-tight">
            Request a cycling-friendly stay at Malak BnB Resort
          </h2>
          <p className="mt-5 text-primary-foreground/70 max-w-lg mx-auto leading-relaxed">
            Submit a reservation request and mention your dates and any bike storage needs. The team will personally confirm availability.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/book" className="inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg">
              Request Your Stay
            </Link>
            <a href="tel:+12899298055" className="inline-flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground text-sm font-medium transition-colors">
              <Phone className="h-4 w-4" />
              1-289-929-8055
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
